import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

export function usePatientData(userId) {
  const [patientId, setPatientId] = useState(null)
  const [patientNombre, setPatientNombre] = useState(null)
  const [goals, setGoals] = useState(null)
  const [todayLog, setTodayLog] = useState(null)
  const [meals, setMeals] = useState([])
  const [metrics, setMetrics] = useState([])
  const [activity, setActivity] = useState([])
  const [loading, setLoading] = useState(true)
  const [balanceHistory, setBalanceHistory] = useState([])
  const [plan, setPlan] = useState(null)
  const [activityProfile, setActivityProfile] = useState(null)
  const [targetsHistory, setTargetsHistory] = useState([])

  const today = new Date().toISOString().split('T')[0]

  useEffect(() => {
    if (userId) init()
  }, [userId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function init() {
    setLoading(true)
    const { data: pat } = await supabase
      .from('patients')
      .select('id, nombre')
      .eq('user_id', userId)
      .single()

    if (!pat) { setLoading(false); return }
    setPatientId(pat.id)
    setPatientNombre(pat.nombre)
    await Promise.all([
      fetchGoals(pat.id),
      fetchTodayLog(pat.id),
      fetchMetrics(pat.id),
      fetchActivity(pat.id),
      fetchBalanceHistory(pat.id).then(setBalanceHistory),
      fetchPlan(pat.id),
      fetchActivityProfile(pat.id),
      fetchTargetsHistory(pat.id),
    ])
    setLoading(false)
  }

  async function fetchGoals(pid) {
    const { data } = await supabase
      .from('patient_goals')
      .select('*')
      .eq('paciente_id', pid)
      .single()
    setGoals(data)
  }

  async function fetchTodayLog(pid) {
    let { data: log } = await supabase
      .from('daily_logs')
      .select('id')
      .eq('paciente_id', pid)
      .eq('fecha', today)
      .single()

    if (!log) {
      const { data: newLog } = await supabase
        .from('daily_logs')
        .insert({ paciente_id: pid, fecha: today })
        .select('id')
        .single()
      log = newLog
    }

    setTodayLog(log)
    if (log) await fetchMeals(log.id)
  }

  async function fetchMeals(logId) {
    const { data } = await supabase
      .from('meals')
      .select(`
        id, tipo,
        meal_items (
          id, cantidad_gramos,
          foods ( id, nombre, kcal_100g, proteina_100g, grasa_100g, carbohidratos_100g )
        )
      `)
      .eq('daily_log_id', logId)
    setMeals(data ?? [])
  }

  async function fetchMetrics(pid) {
    const { data } = await supabase
      .from('body_metrics')
      .select('*')
      .eq('paciente_id', pid)
      .order('fecha', { ascending: false })
    setMetrics(data ?? [])
  }

  async function fetchActivity(pid) {
    const { data } = await supabase
      .from('activity_logs')
      .select('*')
      .eq('paciente_id', pid)
      .eq('fecha', today)
      .order('created_at', { ascending: false })
    setActivity(data ?? [])
  }

  async function fetchBalanceHistory(pid, days = 14) {
    const desde = new Date()
    desde.setDate(desde.getDate() - days)
    const desdeStr = desde.toISOString().split('T')[0]

    const { data } = await supabase
      .from('daily_logs')
      .select(`
        fecha,
        meals (
          meal_items (
            cantidad_gramos,
            foods ( kcal_100g, proteina_100g, grasa_100g, carbohidratos_100g )
          )
        )
      `)
      .eq('paciente_id', pid)
      .gte('fecha', desdeStr)
      .order('fecha', { ascending: true })

    if (!data) return []

    return data.map(log => {
      const items = log.meals?.flatMap(m => m.meal_items ?? []) ?? []
      const kcal = items.reduce((s, i) => s + (i.foods?.kcal_100g * i.cantidad_gramos / 100 || 0), 0)
      const prot = items.reduce((s, i) => s + (i.foods?.proteina_100g * i.cantidad_gramos / 100 || 0), 0)
      const gras = items.reduce((s, i) => s + (i.foods?.grasa_100g * i.cantidad_gramos / 100 || 0), 0)
      const carb = items.reduce((s, i) => s + (i.foods?.carbohidratos_100g * i.cantidad_gramos / 100 || 0), 0)
      return {
        fecha: log.fecha,
        label: new Date(log.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
        kcal: Math.round(kcal),
        prot: Math.round(prot),
        gras: Math.round(gras),
        carb: Math.round(carb),
      }
    })
  }

  async function addMealItem(tipo, foodId, cantidadGramos) {
    let meal = meals.find(m => m.tipo === tipo)
    if (!meal) {
      const { data } = await supabase
        .from('meals')
        .insert({ daily_log_id: todayLog.id, tipo })
        .select('id, tipo, meal_items(*)')
        .single()
      meal = { ...data, meal_items: [] }
    }
    await supabase.from('meal_items').insert({
      meal_id: meal.id,
      food_id: foodId,
      cantidad_gramos: cantidadGramos,
    })
    await fetchMeals(todayLog.id)
  }

  async function fetchActivityProfile(pid) {
    const { data } = await supabase
      .from('activity_profile')
      .select('*')
      .eq('paciente_id', pid)
      .maybeSingle()
    setActivityProfile(data)
  }

  async function fetchTargetsHistory(pid) {
    const { data } = await supabase
      .from('patient_targets_history')
      .select('kcal_objetivo, tdee, vigente_desde')
      .eq('paciente_id', pid)
      .order('vigente_desde', { ascending: true })
    setTargetsHistory(data ?? [])
  }

  async function fetchPlan(pid) {
    const { data } = await supabase
      .from('patient_plans')
      .select('contenido')
      .eq('paciente_id', pid)
      .maybeSingle()
    setPlan(data?.contenido ?? '')
  }

  async function updateMealItem(itemId, cantidadGramos) {
    await supabase.from('meal_items').update({ cantidad_gramos: cantidadGramos }).eq('id', itemId)
    await fetchMeals(todayLog.id)
  }

  async function deleteMealItem(itemId) {
    await supabase.from('meal_items').delete().eq('id', itemId)
    await fetchMeals(todayLog.id)
  }

  async function addCustomFood(foodData) {
    const { data, error } = await supabase.from('foods').insert(foodData).select().single()
    if (error) throw error
    return data
  }

  async function addMetric(metricData, fecha) {
    await supabase.from('body_metrics').insert({
      paciente_id: patientId,
      registrado_por: userId,
      fecha: fecha ?? today,
      ...metricData,
    })
    await fetchMetrics(patientId)
  }

  async function addActivity(activityData) {
    await supabase.from('activity_logs').insert({
      paciente_id: patientId,
      registrado_por: userId,
      fecha: today,
      ...activityData,
    })
    await fetchActivity(patientId)
  }

  async function searchFoods(query) {
    const { data: local } = await supabase
      .from('foods')
      .select('*')
      .ilike('nombre', `%${query}%`)
      .limit(5)

    const { searchUSDA } = await import('../services/usdaSearch')
    const usda = await searchUSDA(query)

    const localIds = (local ?? []).map(f => f.nombre.toLowerCase())
    const usdaFiltered = usda.filter(f => !localIds.includes(f.nombre.toLowerCase()))

    return [...(local ?? []), ...usdaFiltered].slice(0, 12)
  }

  return {
    patientId, patientNombre, goals, todayLog, meals, metrics, activity, balanceHistory, plan, activityProfile, targetsHistory,
    loading, addMealItem, updateMealItem, deleteMealItem, addCustomFood, addMetric, addActivity, searchFoods,
    fetchBalanceHistory,
    refetch: init,
  }
}